import React, { useEffect, useRef } from 'react';
import Quote from '../components/Quote';
import { Link } from 'react-router-dom';
import { SiteContent } from '../content';


const About: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.01, rootMargin: "0px 0px 100px 0px" });

    const elements = containerRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach(el => {
      if (el.getBoundingClientRect().top >= window.innerHeight) {
        el.classList.add('below-fold');
        observer.observe(el);
      }
    });

    return () => observer.disconnect();
  }, []);

  const { hero, why, reframe, mechanism, transformation, community, closing } = SiteContent.about;

  return (
    <div ref={containerRef} className="flex flex-col w-full bg-paper overflow-hidden">
      {/* SECTION 1: HERO */}
      <section className="relative min-h-[60vh] md:h-[80vh] flex items-center justify-center overflow-hidden py-32 md:py-40 bg-rich-black">
        <img
          src="/images/Community-Gather-Create.jpg"
          alt="Community gathering to create earth-centered business"
          className="absolute inset-0 w-full h-full object-cover opacity-75"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 text-center max-w-5xl px-6">
          <div className="animate-on-scroll animate-fade-up">
            <span className="editorial-label !text-white/70 mb-8 block">{hero.title}</span>
            <Quote large text={hero.quote} color="cream" className="drop-shadow-xl" />
          </div>
        </div>
      </section>

      {/* SECTION 2: THE WHY */}
      <section className="section-padding bg-paper relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          <div>
            <div className="animate-on-scroll animate-fade-up">
              <h2 className="section-title text-4xl md:text-6xl lg:text-7xl mb-8 leading-none">{why.title}</h2>
            </div>
            <div className="animate-on-scroll animate-fade-up delay-100">
              <p className="serif italic text-xl md:text-2xl text-[#A65D3F] leading-relaxed mb-8 max-w-lg">
                {why.opener}
              </p>
            </div>
            <div className="animate-on-scroll animate-fade-up delay-200">
              <p className="text-lg md:text-xl font-medium text-[#1A1A1A]/80 leading-relaxed mb-8 max-w-lg">
                {why.text}
              </p>
            </div>
            <div className="animate-on-scroll animate-fade-up delay-300">
              <Quote text={why.quote} align="left" className="px-0" />
            </div>
          </div>
          <div className="flex items-center justify-center">
            <div className="relative animate-on-scroll animate-fade-up w-[26rem] h-[26rem] md:w-[33.8rem] md:h-[33.8rem] rounded-full overflow-hidden shadow-2xl">
              <img src="/images/value.jpg" alt="Value over money" loading="lazy" className="w-full h-full object-cover object-center scale-[1.18]" />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: THE REFRAME */}
      <section className="section-padding bg-white relative overflow-hidden">
        <img
          src="/images/Technician-of-the-Sacred-Woodcut-Print-Style.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-[0.2] mix-blend-multiply"
        />
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-12 items-center relative z-10">
          <div className="md:col-span-5">
            <span className="editorial-label">THE SOLUTION</span>
            <h2 className="section-title mb-8">{reframe.title}</h2>
            <p className="text-lg md:text-xl font-medium text-[#1A1A1A]/80 leading-relaxed mb-8">
              {reframe.text}
            </p>
          </div>
          <div className="md:col-span-1"></div>
          <div className="md:col-span-6 space-y-12">
            {reframe.quotes.map((quote, i) => (
              <div key={i} className="animate-on-scroll animate-fade-up" style={{ transitionDelay: `${i * 100}ms` }}>
                <Quote text={quote} align="left" size="lg" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4: THE MECHANISM (NEW) */}
      <section className="py-24 md:py-40 bg-rich-black relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-20 items-center">
            <div>
              <span className="editorial-label !text-white/60">THE MECHANISM</span>
              <h2 className="section-title text-4xl md:text-5xl text-white mb-8">{mechanism.title}</h2>
              <p className="text-white/60 text-lg md:text-xl mb-12">{mechanism.description}</p>

              <ul className="space-y-6">
                {mechanism.items.map((item, i) => (
                  <li key={i} className="animate-on-scroll animate-fade-up" style={{ transitionDelay: `${i * 100}ms` }}>
                    <span className="text-white/80 text-xs font-bold tracking-widest uppercase">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/5 p-12 rounded-2xl border border-white/10 animate-on-scroll animate-scale-in delay-300">
              <Quote text={mechanism.quote} large color="terracotta" />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: THE TRANSFORMATION PATH */}
      <section className="section-padding bg-paper">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="editorial-label">THE JOURNEY</span>
          <div className="animate-on-scroll animate-fade-up">
            <h2 className="section-title mb-16">{transformation.title}</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {transformation.stages.map((stage: { title: string; description: string }, i: number) => (
              <div key={i} className="group p-10 bg-white/50 border border-black/5 hover:bg-white transition-all duration-700 animate-on-scroll animate-scale-in" style={{ transitionDelay: `${i * 150}ms` }}>
                <div className="text-5xl font-black text-[#A65D3F] mb-6 opacity-30">0{i + 1}</div>
                <h3 className="font-condensed text-xl font-black tracking-widest uppercase mb-4">{stage.title}</h3>
                <p className="serif italic text-lg text-[#1A1A1A]/70 leading-relaxed">{stage.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6: COMMUNITY */}
      <section className="section-padding bg-paper">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12 items-center">
          <div className="animate-on-scroll animate-fade-up aspect-square rounded-xl overflow-hidden shadow-2xl relative">
            <img
              src="/images/decentral.jpg"
              alt="Decentralized down to the dirt"
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <div className="animate-on-scroll animate-fade-up">
              <h2 className="section-title text-4xl md:text-6xl lg:text-7xl mb-8 leading-none">{community.title}</h2>
            </div>

            <ul className="space-y-4 mb-10">
              {community.items.map((item, i) => (
                <li key={i} className="font-bold text-xs tracking-widest uppercase opacity-60 flex items-center gap-3">
                  <span className="w-8 h-[1px] bg-[#1A1A1A]"></span>
                  {item}
                </li>
              ))}
            </ul>

            <div className="animate-on-scroll animate-fade-up delay-200">
              <p className="text-lg md:text-xl font-medium text-[#1A1A1A]/80 leading-relaxed mb-10 border-l-4 border-[#A65D3F] pl-6">
                {community.text}
              </p>
            </div>
            <div className="animate-on-scroll animate-fade-up delay-300">
              <p className="text-xl font-serif italic text-[#A65D3F]">{community.quote}</p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: CLOSING */}
      <section className="section-padding bg-rich-black text-center relative overflow-hidden">
        <img
          src="/images/home-hero.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="animate-on-scroll animate-fade-up">
            <Quote large text={closing.quote} color="cream" className="mb-10 drop-shadow-lg" />
          </div>
          <div className="pt-4 animate-on-scroll animate-scale-in delay-200">
            <Link to="/shop" className="btn-primary !px-12">{SiteContent.common.joinMovement}</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;